<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import { searchProducts } from '@/api/algolia';
import HorizontalProductCard from '@/components/base/layout/HorizontalProductCard.vue';
import RouteLink from '@/components/base/RouteLink.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import LargeBodyText from '@/components/base/typography/LargeBodyText.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import money from '@/filters/money';
import { displayPrice, ProductCard, ProductCardData } from '@/utils/productCard';

const props = withDefaults(
  defineProps<{
    maximumProducts?: number;
    skus: string[];
  }>(),
  { maximumProducts: 2 },
);

const router = useRouter();
const productCards = ref<ProductCardData[]>([]);

const loadProducts = async () => {
  const displayProducts = props.skus.slice(0, props.maximumProducts);
  const productSkus = displayProducts.map((sku) => `sku:${sku}`);

  const algoliaProducts = await searchProducts({
    filters: 'traits:primary',
    facetFilters: [productSkus],
  });

  productCards.value = algoliaProducts.hits.map(ProductCard.fromAlgolia);
};

onMounted(() => {
  watch(
    () => props.skus,
    async () => {
      try {
        await loadProducts();
      } catch (error) {
        console.error('failed to get Algolia results', error);
        productCards.value = [];
      }
    },
    { immediate: true },
  );
});
</script>

<template>
  <div v-if="productCards.length > 0">
    <div class="flex items-center">
      <LargeBodyText class="font-semibold" data-test="upsell-header">Go Deluxe</LargeBodyText>
      <img class="w-5 ml-1" alt="rounded arrow" src="@/assets/pdp/rounded-arrow.svg" />
    </div>
    <div
      v-for="productCard in productCards"
      :key="productCard.sku"
      class="p-4 mt-2 border border-solid rounded-md shadow-md cursor-pointer md:mt-3 border-nuts-neutral-400"
      data-test="upsell-product"
      @click="router.push(productCard.path)"
    >
      <HorizontalProductCard
        imageSize="medium"
        :product="productCard"
        data-promo="1"
        :data-promo-creative="productCard.name"
        data-promo-name="Upselling Products"
      >
        <template v-slot:price>
          <BaseBodyText
            class="font-semibold font-sofia-pro"
            data-test="upsell-price"
            :class="
              productCard.totalSavings?.onSale ? 'text-nuts-red-800' : 'text-nuts-neutral-800'
            "
          >
            {{ productCard.hidePrice ? 'Build your own' : displayPrice(productCard) }}
          </BaseBodyText>
          <SmallBodyText
            v-if="productCard.totalSavings"
            class="ml-1.5 line-through text-neutral-500"
            data-test="upsell-product-ratings"
          >
            {{ money(productCard.totalSavings?.comparisonPrice) }}
          </SmallBodyText>
        </template>
        <template v-slot:button>
          <RouteLink
            class="block mt-2 underline nuts-cyan-800"
            data-test="shop-now"
            :to="productCard.path"
          >
            <SmallBodyText>Shop Now</SmallBodyText>
          </RouteLink>
        </template>
      </HorizontalProductCard>
    </div>
  </div>
</template>
